import { useState, useRef, useCallback, useMemo } from 'react';

type SwipeHandlers = {
  onTouchStart: (e: React.TouchEvent) => void;
  onTouchMove: (e: React.TouchEvent) => void;
  onTouchEnd: () => void;
};

type SlideDirection = 'left' | 'right' | null;

const SWIPE_THRESHOLD = 50;

export const useSwipeTabs = (tabCount: number) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [slideDirection, setSlideDirection] = useState<SlideDirection>(null);
  const startX = useRef(0);
  const isDragging = useRef(false);
  const dragDelta = useRef(0);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    startX.current = e.touches[0].clientX;
    isDragging.current = true;
    dragDelta.current = 0;
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (!isDragging.current) return;
    dragDelta.current = e.touches[0].clientX - startX.current;
  }, []);

  const handleTouchEnd = useCallback(() => {
    if (!isDragging.current) return;
    isDragging.current = false;

    const delta = dragDelta.current;

    if (delta < -SWIPE_THRESHOLD && activeIndex < tabCount - 1) {
      setSlideDirection('left');
      setActiveIndex(prev => prev + 1);
    } else if (delta > SWIPE_THRESHOLD && activeIndex > 0) {
      setSlideDirection('right');
      setActiveIndex(prev => prev - 1);
    }

    dragDelta.current = 0;
  }, [activeIndex, tabCount]);

  const handlers: SwipeHandlers = useMemo(() => ({
    onTouchStart: handleTouchStart,
    onTouchMove: handleTouchMove,
    onTouchEnd: handleTouchEnd,
  }), [handleTouchStart, handleTouchMove, handleTouchEnd]);

  const goToTab = useCallback((index: number) => {
    if (index === activeIndex) return;
    setSlideDirection(index > activeIndex ? 'left' : 'right');
    setActiveIndex(index);
  }, [activeIndex]);

  const clearSlideDirection = useCallback(() => {
    setSlideDirection(null);
  }, []);

  return { activeIndex, goToTab, handlers, slideDirection, clearSlideDirection };
};
